import type { AuthUser } from '@admitly/shared';
import {
  canSeeCatalogueOverview,
  canSeeCommissionConsole,
  canSeeCommissionEarnings,
  canSeeConsultantPricingLink,
  canSeeOversightQuotes,
  canSeePricingConsole,
  canSeePrivateCourses,
  canSeeQuotes,
  canSeeTeam,
} from './capabilities.js';

/**
 * The sidebar as PURE DATA — every visibility and placement decision the nav
 * makes, as an ordered list of sections, with no React and no router (Brief 8c).
 *
 * WHY THIS EXISTS: Brief 8b re-based each link's gate from role to CAPABILITY,
 * but the gate lived on the <Link> while the section around it stayed
 * role-gated. A consultant granted edit_commission was admitted by the
 * /app/admin/commission route guard yet never saw the link — she draws no admin
 * block. The predicate moved; the placement didn't, and Sidebar.tsx was
 * untestable-by-render (the RouterProvider seam), so no test could catch it.
 *
 * The cure is to make placement itself a value you can assert. buildSidebar is
 * the single source of truth for what appears and where; Sidebar.tsx is a dumb
 * renderer of what this returns; sidebar-links.test.ts pins the whole ordered
 * list. This is a DISPLAY rule, not a gate — the server (requireCapability) is
 * still the only thing that grants access. See lib/capabilities.ts.
 *
 * Icons and routes are carried as KEYS/strings so this module imports nothing
 * from React or @tanstack/react-router — the renderer maps a key to a component.
 */

/** Tabler icon, referenced by key so this module stays render-framework-free. */
export type SidebarIconKey =
  | 'search'
  | 'sparkles'
  | 'star'
  | 'coin'
  | 'file-invoice'
  | 'building-bank'
  | 'book2'
  | 'list-check'
  | 'building-community'
  | 'gift'
  | 'chart-bar'
  | 'history'
  | 'user-shield'
  | 'user-cog'
  | 'percentage'
  | 'users'
  | 'palette'
  | 'tags'
  | 'credit-card'
  | 'mail';

/** Exactly the 23 static routes the sidebar links target (none take params). */
export type SidebarRoute =
  | '/app/search'
  | '/app/suggest'
  | '/app/shortlist'
  | '/app/pricing'
  | '/app/quotes'
  | '/app/commission/earnings'
  | '/app/oversight/quotes'
  | '/app/oversight/catalogue'
  | '/app/admin/providers'
  | '/app/admin/courses'
  | '/app/admin/checklist'
  | '/app/admin/tenants'
  | '/app/admin/agencies'
  | '/app/admin/analytics'
  | '/app/admin/audit-log'
  | '/app/admin/users'
  | '/app/admin/staff'
  | '/app/admin/pricing'
  | '/app/admin/commission'
  | '/app/admin/private-courses'
  | '/app/admin/team'
  | '/app/admin/branding'
  | '/app/admin/categories'
  | '/app/admin/billing'
  | '/app/admin/email-sender';

export interface SidebarItem {
  key: string;
  label: string;
  to: SidebarRoute;
  icon: SidebarIconKey;
}

export interface SidebarSection {
  key: string;
  heading: string;
  items: SidebarItem[];
}

// The two capability-gated CONSOLE editors. Shared constants so the Admin block
// and the Admin-tools section (below) carry byte-identical items in the same
// relative order — that ordering is the contract, not a coincidence.
const PRICING_CONSOLE: SidebarItem = {
  key: 'admin-pricing',
  label: 'Course Pricing',
  to: '/app/admin/pricing',
  icon: 'coin',
};
const COMMISSION_CONSOLE: SidebarItem = {
  key: 'admin-commission',
  label: 'Commission',
  to: '/app/admin/commission',
  icon: 'percentage',
};
// The read-only commission-earnings report (Brief BUILD-COMMISSION-VIEW). Gated
// on view_commission (canSeeCommissionEarnings), so it appears for
// super_admin/admin (alongside the editor console above) AND for owner/accounts
// (who hold view_commission but not edit_commission) in their Oversight section.
const COMMISSION_EARNINGS: SidebarItem = {
  key: 'commission-earnings',
  label: 'Commission earnings',
  to: '/app/commission/earnings',
  icon: 'percentage',
};
// The agency-private catalogue link. Shared so the Admin block and the
// Admin-tools section carry a byte-identical item (Brief TIDY-1 surfaces it for
// a non-admin who holds manage_catalogue, matching its capability-based guard).
const PRIVATE_COURSES: SidebarItem = {
  key: 'admin-private-courses',
  label: 'Private courses',
  to: '/app/admin/private-courses',
  icon: 'book2',
};

/**
 * Build the ordered sidebar for a user. A section is included only when it has
 * ≥1 visible item. Pure over `user` — the dynamic category tree, the Agencies
 * pending-count badge, active state and the footer are the renderer's job, not
 * this list's, because they are not a function of the user alone.
 */
export function buildSidebar(user: AuthUser | null | undefined): SidebarSection[] {
  const role = user?.role;
  const sections: SidebarSection[] = [];

  // --- Catalogue — always present (the three top links are ungated). ---
  const catalogue: SidebarItem[] = [
    { key: 'search', label: 'Find courses', to: '/app/search', icon: 'search' },
    { key: 'suggest', label: 'Suggest courses', to: '/app/suggest', icon: 'sparkles' },
    { key: 'shortlist', label: 'My shortlist', to: '/app/shortlist', icon: 'star' },
  ];
  // Role AND capability: role picks the VARIANT (this read-only /app/pricing is
  // the consultant's; admins have the console), view_pricing is the PERMISSION.
  if (canSeeConsultantPricingLink(user)) {
    catalogue.push({ key: 'consultant-pricing', label: 'Course pricing', to: '/app/pricing', icon: 'coin' });
  }
  // `role !== 'admin'` is POSITIONING, not permission — admins get their Quotes
  // link inside the Admin block below and must not get two. Everyone else who
  // may quote (incl. a granted offshore user) gets it here.
  if (role !== 'admin' && canSeeQuotes(user)) {
    catalogue.push({ key: 'quotes', label: 'Quotes', to: '/app/quotes', icon: 'file-invoice' });
  }
  sections.push({ key: 'catalogue', heading: 'Catalogue', items: catalogue });

  // --- Admin — super_admin variant. ---
  if (role === 'super_admin') {
    const items: SidebarItem[] = [
      { key: 'admin-providers', label: 'Providers', to: '/app/admin/providers', icon: 'building-bank' },
      { key: 'admin-courses', label: 'Courses', to: '/app/admin/courses', icon: 'book2' },
      { key: 'admin-checklist', label: 'Checklist', to: '/app/admin/checklist', icon: 'list-check' },
      { key: 'admin-tenants', label: 'Tenants', to: '/app/admin/tenants', icon: 'building-community' },
      { key: 'admin-agencies', label: 'Agencies', to: '/app/admin/agencies', icon: 'gift' },
      { key: 'admin-analytics', label: 'Analytics', to: '/app/admin/analytics', icon: 'chart-bar' },
      { key: 'admin-audit-log', label: 'Audit log', to: '/app/admin/audit-log', icon: 'history' },
      // Cross-agency quotes oversight (Brief QUOTES-OVERSIGHT) — super_admin only,
      // READ-ONLY. Lives beside Audit log, the other cross-agency read surface,
      // and reuses the owner/accounts overview screen with cross-agency scope.
      { key: 'admin-quotes-oversight', label: 'Quotes', to: '/app/oversight/quotes', icon: 'file-invoice' },
      { key: 'admin-users', label: 'Users', to: '/app/admin/users', icon: 'user-shield' },
      { key: 'admin-staff', label: 'Staff', to: '/app/admin/staff', icon: 'user-cog' },
    ];
    if (canSeePricingConsole(user)) items.push(PRICING_CONSOLE);
    if (canSeeCommissionConsole(user)) items.push(COMMISSION_CONSOLE);
    if (canSeeCommissionEarnings(user)) items.push(COMMISSION_EARNINGS);
    sections.push({ key: 'admin', heading: 'Admin', items });
  }

  // --- Admin — agency-admin variant. ---
  if (role === 'admin') {
    const items: SidebarItem[] = [];
    if (canSeePrivateCourses(user)) {
      items.push(PRIVATE_COURSES);
    }
    // Team is capability-gated on manage_users (Brief T1 — closes the mismatch
    // flagged, not patched, in Brief 8b/8c). The `admin` preset lacks
    // manage_users (super_admin only) yet this screen's API requires it, so a
    // bare admin used to get a link that only ever showed 403 banners; now the
    // link tracks the switch (an admin granted manage_users keeps it). canSeeTeam
    // also keeps the role condition — the UI stays STRICTER than the server
    // (fail-closed), so a granted non-admin gains no Team surface.
    if (canSeeTeam(user)) {
      items.push({ key: 'admin-team', label: 'Team', to: '/app/admin/team', icon: 'users' });
    }
    items.push({ key: 'admin-branding', label: 'Branding', to: '/app/admin/branding', icon: 'palette' });
    items.push({ key: 'admin-categories', label: 'Categories', to: '/app/admin/categories', icon: 'tags' });
    items.push({ key: 'admin-billing', label: 'Billing', to: '/app/admin/billing', icon: 'credit-card' });
    if (canSeePricingConsole(user)) items.push(PRICING_CONSOLE);
    if (canSeeCommissionConsole(user)) items.push(COMMISSION_CONSOLE);
    if (canSeeCommissionEarnings(user)) items.push(COMMISSION_EARNINGS);
    if (canSeeQuotes(user)) {
      items.push({ key: 'admin-quotes', label: 'Quotes', to: '/app/quotes', icon: 'file-invoice' });
    }
    items.push({ key: 'admin-email-sender', label: 'Email sender', to: '/app/admin/email-sender', icon: 'mail' });
    items.push({ key: 'admin-analytics', label: 'Analytics', to: '/app/admin/analytics', icon: 'chart-bar' });
    items.push({ key: 'admin-audit-log', label: 'Audit log', to: '/app/admin/audit-log', icon: 'history' });
    sections.push({ key: 'admin', heading: 'Admin', items });
  }

  // --- Oversight — owner/accounts read-only cross-branch screens (Brief OA). ---
  // The two links owner and accounts finally land on: a cross-branch quotes
  // overview (view_all_branches) and a read-only catalogue/pricing overview
  // (view_pricing). Scoped to non-admin/non-super_admin so admin/super_admin —
  // who also hold view_all_branches — never render them here; they read quotes
  // through their own screens. A bare consultant/offshore holds neither predicate,
  // so the section is empty for them and is skipped.
  if (role !== 'admin' && role !== 'super_admin') {
    const oversight: SidebarItem[] = [];
    if (canSeeOversightQuotes(user)) {
      oversight.push({ key: 'oversight-quotes', label: 'Quotes overview', to: '/app/oversight/quotes', icon: 'file-invoice' });
    }
    if (canSeeCatalogueOverview(user)) {
      oversight.push({ key: 'oversight-catalogue', label: 'Catalogue overview', to: '/app/oversight/catalogue', icon: 'coin' });
    }
    // Commission earnings — owner/accounts hold view_commission (admin/super_admin
    // are excluded from this section and get the link in their Admin block above).
    if (canSeeCommissionEarnings(user)) {
      oversight.push(COMMISSION_EARNINGS);
    }
    if (oversight.length > 0) sections.push({ key: 'oversight', heading: 'Oversight', items: oversight });
  }

  // --- Admin tools — the fix. ---
  // A non-admin (consultant/owner/accounts/offshore) whose switches unlock an
  // admin link had nowhere to show it, because those links only lived inside the
  // role-gated Admin blocks above. Surface them here in the Admin block's
  // relative order (private courses, then pricing before commission), after the
  // user's own sections. Admin/super_admin keep their links where they are — no
  // Admin tools for them.
  //
  // Private courses is included (Brief TIDY-1): its guard is capability-based
  // (canSeePrivateCourses, no role check) and admits any non-admin holding
  // manage_catalogue, so the link now consults the SAME predicate the guard
  // enforces — closing the reachable-by-URL-but-no-link gap the T1 note left.
  if (role !== 'admin' && role !== 'super_admin') {
    const tools: SidebarItem[] = [];
    if (canSeePrivateCourses(user)) tools.push(PRIVATE_COURSES);
    if (canSeePricingConsole(user)) tools.push(PRICING_CONSOLE);
    if (canSeeCommissionConsole(user)) tools.push(COMMISSION_CONSOLE);
    if (tools.length > 0) sections.push({ key: 'admin-tools', heading: 'Admin tools', items: tools });
  }

  return sections;
}
